/**
 * Event Builder — autosave / publish engine (plan 3.4).
 *
 *  - one request in flight at a time; per-step 900 ms debounce; queue ordered type → details →
 *    availability → advanced; duplicates coalesce (rule 2)
 *  - nothing is sent for a new event until the type step validates; the first type save stores the
 *    id and rewrites the URL (rule 1)
 *  - full payloads only (rule 3); `keep_draft: 'E'` on advanced while drafting (rule 4)
 *  - publish = flush + validate all + four sequential saves without keep_draft + type refetch (rule 5)
 *  - 422 → field errors; any 403 is fatal (rule 7 / 8.3): the interceptor is navigating away
 *  - a step with client validation issues is never enqueued (rule 10)
 *  - kind switch on an existing event re-hydrates details after the type save (rule 13)
 *
 * The engine is a plain object; the React layer feeds it the latest state through `getState`
 * and reads results back through the reducer (the engine only dispatches).
 *
 * `save.status` meaning: idle = nothing to send (or blocked by validation) · saving = queued or
 * in flight · saved = everything acknowledged · error = last request failed (retry available).
 */

import { isSmsAllowed } from "../../../../utils/trialAccess";
import { actions } from "./builderReducer";
import { D, E, SECTIONS, STEPS, STEP_ORDER, STEP_TO_NAV_SECTION } from "./constants";
import {
  errorBody,
  errorStatus,
  getDetailsPage,
  getTypePage,
  isFatal403,
  saveAdvancedPage,
  saveAvailabilityPage,
  saveDetailsPage,
  saveTypePage,
} from "./api";
import { toAdvancedPayload, toAvailabilityPayload, toDetailsPayload, toTypePayload } from "./payloads";
import { hydrateDetails } from "./hydrate";
import { mapServerError, validateAdvanced, validateAvailability, validateDetails, validateType } from "./validation";

export const DEBOUNCE_MS = 900;
const READY_RETRY_MS = 400;

export const reduxReady = (redux) => Boolean(redux && redux.featuresLoaded && redux.userPlan);

export function validateStep(step, state, redux) {
  const { type, details, availability, advanced, meta, lookups } = state;
  const { userPlan, features, limits } = redux || {};
  switch (step) {
    case STEPS.TYPE:
      return validateType(type, meta, lookups, features);
    case STEPS.DETAILS:
      return validateDetails(details, meta, features);
    case STEPS.AVAILABILITY:
      return validateAvailability(availability, meta, lookups, { userPlan, features, limits });
    case STEPS.ADVANCED:
      return validateAdvanced(advanced, meta, lookups, { features, limits });
    default:
      return [];
  }
}

export function buildPayload(step, state, redux, { publish = false } = {}) {
  const { type, details, availability, advanced, meta, lookups } = state;
  const { userPlan, features, featureAccess } = redux || {};
  switch (step) {
    case STEPS.TYPE:
      return toTypePayload(type, meta);
    case STEPS.DETAILS:
      return toDetailsPayload(details, meta, features);
    case STEPS.AVAILABILITY:
      return toAvailabilityPayload(availability, meta, userPlan, features);
    case STEPS.ADVANCED:
      return toAdvancedPayload(advanced, meta, lookups, features, isSmsAllowed(userPlan, featureAccess), {
        keepDraft: !publish && meta.isDraft === E,
      });
    default:
      return null;
  }
}

const post = (step, payload) => {
  switch (step) {
    case STEPS.TYPE:
      return saveTypePage(payload);
    case STEPS.DETAILS:
      return saveDetailsPage(payload);
    case STEPS.AVAILABILITY:
      return saveAvailabilityPage(payload);
    case STEPS.ADVANCED:
      return saveAdvancedPage(payload);
    default:
      return Promise.reject(new Error(`unknown step ${step}`));
  }
};

/** Which nav section a server (non-field) error for `step` belongs to (8.3: staff-seat 422 → Hosts). */
export const errorSection = (step, message, fieldErrors) => {
  if (step === STEPS.AVAILABILITY) {
    const text = `${message || ""} ${Object.keys(fieldErrors || {}).join(" ")}`.toLowerCase();
    if (text.includes("staff")) return SECTIONS.HOSTS;
  }
  return STEP_TO_NAV_SECTION[step];
};

/**
 * @param {object} deps
 * @param {() => object} deps.getState     latest reducer state
 * @param {(action) => void} deps.dispatch reducer dispatch
 * @param {() => object} deps.getRedux     { userPlan, features, limits, featuresLoaded, featureAccess }
 * @param {(eventId:number) => void} deps.onFirstSave   navigate(replace) to /builder/{id} + plan usage refresh
 * @param {() => void} deps.onPublished    plan usage refresh after a publish
 */
export function createSaveEngine({ getState, dispatch, getRedux, onFirstSave, onPublished }) {
  const timers = {};
  const pending = new Set();
  let inFlight = false;
  let fatal = false;
  let destroyed = false;
  let readyTimer = null;
  let publishing = false;
  let idleWaiters = [];

  const clearTimer = (step) => {
    if (timers[step]) {
      clearTimeout(timers[step]);
      timers[step] = null;
    }
  };
  const anyTimer = () => STEP_ORDER.some((s) => Boolean(timers[s]));

  const resolveWaiters = () => {
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((w) => w());
  };

  /** Called whenever the queue may have gone quiet. */
  const settleStatus = () => {
    if (destroyed) return;
    const state = getState();
    if (inFlight) return;
    if (state.save.status === "error") {
      resolveWaiters();
      return;
    }
    const typeQueued = pending.has(STEPS.TYPE) || Boolean(timers[STEPS.TYPE]);
    // a new event whose type step is not (yet) sendable: other steps wait silently
    const stalled = !state.meta.eventId && !typeQueued;
    const queued = !stalled && (pending.size > 0 || anyTimer());
    if (queued) {
      dispatch(actions.saveStatus("saving"));
      return;
    }
    const dirty = STEP_ORDER.some((s) => state.save.version[s] > state.save.savedVersion[s]);
    dispatch(actions.saveStatus(dirty ? "idle" : "saved"));
    resolveWaiters();
  };

  const nextStep = () => STEP_ORDER.find((s) => pending.has(s));

  const rehydrateDetailsAfterKindSwitch = async (eventId, kind, eventName) => {
    try {
      const data = await getDetailsPage(eventId, kind === "paid");
      const hydrated = hydrateDetails(data, kind, eventName);
      if (!destroyed) dispatch(actions.replaceStep(STEPS.DETAILS, hydrated.details));
    } catch (e) {
      // keep local values; the local shape already matches the new kind (SET_KIND)
    }
    if (!destroyed) dispatch(actions.setMeta({ kindRehydrate: false }));
  };

  /** Runs one step now. */
  const runStep = async (step, { publish = false } = {}) => {
    const state = getState();
    const redux = getRedux();
    const { meta } = state;

    if (!reduxReady(redux)) return { ok: false, reason: "not-ready" };
    if (!meta.eventId && step !== STEPS.TYPE) return { ok: false, reason: "no-id" }; // rule 1
    if (step === STEPS.DETAILS && meta.kindRehydrate) return { ok: false, reason: "kind-rehydrate" }; // rule 13

    const issues = validateStep(step, state, redux);
    if (issues.length) return { ok: false, reason: "invalid", issues };

    const version = state.save.version[step];
    const payload = buildPayload(step, state, redux, { publish });
    const wasNew = !meta.eventId;

    inFlight = true;
    dispatch(actions.saveStatus("saving"));
    try {
      const result = await post(step, payload);
      if (destroyed) return { ok: false, reason: "destroyed" };

      dispatch(actions.saveSuccess(step, version));
      dispatch(actions.setSectionError(step, null));

      if (step === STEPS.TYPE) {
        const after = getState();
        const eventId = after.meta.eventId || result;
        if (wasNew && result) {
          dispatch(actions.setMeta({ eventId: result, isNew: false }));
          if (onFirstSave) onFirstSave(result);
        }
        if (after.meta.kindRehydrate && eventId) {
          await rehydrateDetailsAfterKindSwitch(eventId, after.meta.kind, after.type.event_name);
        }
      }
      return { ok: true, result };
    } catch (error) {
      if (destroyed) return { ok: false, reason: "destroyed" };
      const status = errorStatus(error);
      const body = errorBody(error);
      const { fieldErrors, message } = mapServerError(step, body);

      if (isFatal403(error)) {
        // 8.3: the interceptor is redirecting; stop autosave so no further writes follow
        fatal = true;
        pending.clear();
        STEP_ORDER.forEach(clearTimer);
        dispatch(actions.saveStatus("error", message));
        return { ok: false, reason: "fatal", status, message };
      }

      if (Object.keys(fieldErrors).length) {
        dispatch(actions.setFieldErrors(step, fieldErrors));
      }
      dispatch(actions.setSectionError(step, { section: errorSection(step, message, fieldErrors), message }));
      dispatch(actions.saveStatus("error", message));
      return { ok: false, reason: "http", status, message, fieldErrors };
    } finally {
      inFlight = false;
    }
  };

  const pump = async () => {
    if (destroyed || fatal || inFlight || publishing) return;
    const step = nextStep();
    if (!step) {
      settleStatus();
      return;
    }

    if (!reduxReady(getRedux())) {
      // plan/features still loading (rule 10): keep everything pending, try again shortly
      if (!readyTimer) {
        readyTimer = setTimeout(() => {
          readyTimer = null;
          pump();
        }, READY_RETRY_MS);
      }
      return;
    }

    const state = getState();
    let target = step;
    if (!state.meta.eventId) {
      if (!pending.has(STEPS.TYPE)) {
        // other steps wait for the type row (they are re-pumped after the type save)
        settleStatus();
        return;
      }
      target = STEPS.TYPE;
    }

    pending.delete(target);
    const outcome = await runStep(target);
    if (destroyed) return;

    if (outcome.ok) {
      const after = getState();
      // an edit made while in flight keeps the step dirty → send again
      if (after.save.version[target] > after.save.savedVersion[target]) pending.add(target);
      // after the first type save, every dirty step becomes sendable
      if (target === STEPS.TYPE) {
        STEP_ORDER.forEach((s) => {
          if (s !== STEPS.TYPE && after.save.version[s] > after.save.savedVersion[s]) pending.add(s);
        });
      }
      pump();
      return;
    }

    switch (outcome.reason) {
      case "invalid":
        // dropped; the next edit to this step re-schedules it (footer counts the issues)
        pump();
        return;
      case "kind-rehydrate":
      case "not-ready":
        pending.add(target);
        pump();
        return;
      default:
        // http / fatal: the step stays dirty (version > savedVersion), status is 'error';
        // nothing else goes out until retry() or the next edit
        pending.clear();
        resolveWaiters();
    }
  };

  // ---- public API -----------------------------------------------------------------------------------

  /** Call after every user edit of `step` (UPDATE_STEP). */
  const noteChange = (step) => {
    if (destroyed || fatal) return;
    dispatch(actions.clearFieldErrors(step));
    dispatch(actions.setSectionError(step, null));
    clearTimer(step);
    timers[step] = setTimeout(() => {
      timers[step] = null;
      pending.add(step);
      pump();
    }, DEBOUNCE_MS);
    const state = getState();
    // a new event needs its type row before anything else can go out (rule 1)
    if (!state.meta.eventId && step !== STEPS.TYPE && !timers[STEPS.TYPE]) pending.add(STEPS.TYPE);
    if (state.meta.eventId || step === STEPS.TYPE || pending.has(STEPS.TYPE)) dispatch(actions.saveStatus("saving"));
  };

  /** Send every dirty step now. Resolves when the queue is quiet (also after an error). */
  const flush = () => {
    if (destroyed) return Promise.resolve();
    const state = getState();
    STEP_ORDER.forEach((s) => {
      clearTimer(s);
      if (state.save.version[s] > state.save.savedVersion[s]) pending.add(s);
    });
    if (!state.meta.eventId) pending.add(STEPS.TYPE);
    if (fatal) return Promise.resolve();
    return new Promise((resolve) => {
      idleWaiters.push(resolve);
      pump();
    });
  };

  /** Re-send after a non-fatal error. */
  const retry = () => {
    if (destroyed || fatal) return Promise.resolve();
    dispatch(actions.saveStatus("saving"));
    return flush();
  };

  /**
   * Publish (rule 5). Resolves { ok, issues?, typeData?, error? }.
   * Every step is sent (dirty or not); advanced without keep_draft.
   */
  const publish = async () => {
    if (destroyed || fatal) return { ok: false, error: "unavailable" };
    const redux = getRedux();
    if (!reduxReady(redux)) return { ok: false, error: "not-ready" };

    STEP_ORDER.forEach(clearTimer);
    pending.clear();
    while (inFlight) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 50));
    }
    if (destroyed) return { ok: false, error: "unavailable" };

    publishing = true;
    try {
      const state = getState();
      const issuesByStep = {};
      let count = 0;
      STEP_ORDER.forEach((s) => {
        issuesByStep[s] = validateStep(s, state, redux);
        count += issuesByStep[s].length;
      });
      if (count) return { ok: false, issues: issuesByStep };

      dispatch(actions.markAllDirty());
      for (let i = 0; i < STEP_ORDER.length; i++) {
        const step = STEP_ORDER[i];
        // eslint-disable-next-line no-await-in-loop
        const outcome = await runStep(step, { publish: true });
        if (!outcome.ok) {
          if (outcome.reason === "invalid") return { ok: false, issues: { [step]: outcome.issues } };
          return { ok: false, error: outcome.message || outcome.reason, step, fieldErrors: outcome.fieldErrors };
        }
      }

      const eventId = getState().meta.eventId;
      let typeData = null;
      try {
        typeData = await getTypePage(eventId);
      } catch (e) {
        typeData = null;
      }
      if (destroyed) return { ok: false, error: "unavailable" };
      dispatch(
        actions.setMeta({
          // until backend 7.3 ships `is_draft` is absent → published (the advanced save just cleared it)
          isDraft: typeData && typeData.is_draft === E ? E : D,
          isActive: typeData && typeData.is_active === E ? E : D,
          publishedKind: getState().meta.kind,
        })
      );
      dispatch(actions.saveStatus("saved"));
      if (onPublished) onPublished();
      return { ok: true, typeData };
    } finally {
      publishing = false;
      // edits made during publish were debounced into timers/pending; let them go out
      pump();
    }
  };

  const destroy = () => {
    destroyed = true;
    STEP_ORDER.forEach(clearTimer);
    if (readyTimer) clearTimeout(readyTimer);
    pending.clear();
    resolveWaiters();
  };

  const isBusy = () => inFlight || pending.size > 0 || anyTimer();
  const isFatal = () => fatal;

  return { noteChange, flush, retry, publish, destroy, isBusy, isFatal, pump };
}
